"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createRound = void 0;
/**
* @ai-prompt-initial: Create the new rich domain model for Round as the next step in the TD-008 refactor. This file should be created at `functions-model/src/match/round.model.ts`. It must define the `Round` interface and the `createRound` constructor function. The model should use the `createPlayerRound` factory to create rich objects for players in the attacking and defending hands. It should encapsulate all state-reading logic (e.g., `getAttackingPlayer`, `getDefendingHand`, `getAction`, `getZone`) and must not contain any state mutation logic or dependencies on a parent `Match` object.
* @date: 2025-07-16
*/
const roundState_1 = require("../match/matchstate/roundState");
const action_1 = require("./matchstate/action");
const playerRound_model_1 = require("./playerRound.model");
/**
 * Creates a rich Round object from a raw RoundState.
 *
 * @param {RoundState} state The raw state of the round.
 * @returns {Round} A new Round object with navigation functions.
 */
const createRound = (state) => {
    // Memoize player round creations for efficiency
    const attackingHand = state.attackingHand.map((prs) => (0, playerRound_model_1.createPlayerRound)(prs));
    const defendingHand = state.defendingHand.map((prs) => (0, playerRound_model_1.createPlayerRound)(prs));
    return {
        getUnderlyingState: () => state,
        getRoundId: () => state.roundId,
        getAttackingHand: () => attackingHand,
        getDefendingHand: () => defendingHand,
        getAction: () => state.action,
        getZone: () => state.zone,
        getTargetZones: () => state.targetZone,
        isAttackStall: () => state.action === action_1.Actions.STALL,
        isDefenseStall: () => state.defendingPlayer === -1,
        getContests: () => state.contests,
        getRoundResult: () => state.roundResult,
        getAttackingPlayer: () => {
            return state.attackingPlayer !== undefined
                ? attackingHand[state.attackingPlayer]
                : undefined;
        },
        getDefendingPlayer: () => {
            return state.defendingPlayer !== undefined && state.defendingPlayer > -1
                ? defendingHand[state.defendingPlayer]
                : undefined;
        },
        getAttackingTargetPlayer: () => {
            return state.attackingTargetPlayer !== undefined
                ? attackingHand[state.attackingTargetPlayer]
                : undefined;
        },
        getGoalkeeper: () => {
            return defendingHand.find((player) => player.isGoalie());
        },
        getLastContest: () => {
            if (state.contests.length === 0) {
                return undefined;
            }
            return state.contests[state.contests.length - 1];
        },
        getLastDefender: () => {
            const lastContest = (0, exports.createRound)(state).getLastContest();
            if (!lastContest) {
                return (0, exports.createRound)(state).getDefendingPlayer();
            }
            return defendingHand[lastContest.defendingPlayerIndex];
        },
        getGoalScored() {
            return state.roundResult === roundState_1.RoundResult.GOAL;
        },
        getAttackingTeamId: () => state.attackingTeam,
        getDefendingTeamId: () => state.defendingTeam,
    };
};
exports.createRound = createRound;
